import Image, { type ImageProps } from "next/image";
import type { ReactNode } from "react";

interface SafeImageProps extends Omit<ImageProps, "src" | "alt"> {
  /** Image source coming from the API/props — may legitimately be null/undefined/"" */
  src?: string | null;
  alt?: string | null;
  /** Rendered instead of <Image> when src is missing. Defaults to nothing. */
  fallback?: ReactNode;
}

/**
 * Drop-in replacement for next/image's <Image> that guards against the
 * "Cannot read properties of null (reading 'default')" crash caused by
 * passing a null/undefined/empty src (e.g. a Django API field that can be
 * null: image, logo, thumbnail, photo, icon, ...).
 *
 * - src is intentionally checked with `!src` (not `??`) so an empty string
 *   ("") is treated as missing too.
 * - alt falls back to "" since it can also come back null from the API.
 */
export function SafeImage({ src, alt, fallback = null, ...rest }: SafeImageProps) {
  if (!src) {
    return <>{fallback}</>;
  }

  return <Image src={src} alt={alt ?? ""} {...rest} />;
}
